import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ChevronDown, Crosshair, PencilLine } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import type { StudyGoals } from "@digital-twins/shared-types";

import { MorphText } from "./MorphText";
import type { StudyObjectiveUpdate } from "../hooks/useQuestionnaireWorkspace";
import {
  cleanLearnBullets,
  countStudyObjectiveChars,
  parseStudyObjective,
  serializeStudyObjective,
  STUDY_OBJECTIVE_MAX_CHARS,
  validateStudyObjectiveText,
} from "../objectives/studyObjectiveText";

/**
 * The study objective, at the top of the questionnaire it is the reason for.
 *
 * It shows objectives and nothing else: the design brief that routes the study
 * (method, metric, caveats) steers the planner invisibly and never renders
 * here, and `framing` never renders either. No section labels, no field labels.
 *
 * The header names the objective's phase. `confirmed` is the marketer's
 * turn-zero sentence, verbatim and unshaped, so it reads as theirs ("Your
 * objective"); calling it "Objectives" makes their own words look like a
 * polished objective we wrote. Every other provenance has been through an
 * objective-shaping step (ours when `inferred`, theirs in this row's editor
 * when `edited`) and keeps the plain label.
 *
 * An `inferred` objective is OURS, not the marketer's, so it reads as a
 * suggestion: opens expanded on arrival, muted text, and Edit. There is no
 * confirm step — continuing with the draft as written leaves it `inferred`;
 * Save after Edit is what flips it to `edited`. Anything else opens collapsed,
 * because it is already settled.
 */

/**
 * How close to the cap the quiet character counter appears. The textarea has
 * no `maxLength`: the counter and Save's reason name the cap, counted as the
 * validator counts it, and nothing the marketer types or pastes is cut.
 */
const COUNTER_VISIBLE_FROM = STUDY_OBJECTIVE_MAX_CHARS - 100;

export interface StudyObjectivesRowProps {
  /** The draft's objective. Nothing renders without a decision to show. */
  goals: StudyGoals | null | undefined;
  /** Writes the objective onto the workspace draft (does not persist). */
  onUpdateObjective: (update: StudyObjectiveUpdate) => void;
  /**
   * Sends the objective. Called on the render AFTER the update lands, so it
   * always sends the new objective rather than the one it replaced. Resolves
   * false when nothing was sent (the user kept editing unsaved builder
   * edits); the editor then reopens on the text they saved.
   */
  onSaveObjective: () => Promise<unknown> | void;
  /**
   * Lets the chat's existing Save-and-continue guard protect the local editor
   * buffer before it starts a turn that would replace this row.
   */
  onEditingChange?: (
    editing: boolean,
    savePendingEdit?: () => Promise<boolean>,
  ) => void;
  /**
   * True while the workspace is persisting. Save is disabled during a flight:
   * saveChanges no-ops while saving and then overwrites the workspace draft
   * with its save-time copy, so a commit inside the window would be silently
   * dropped and visually reverted.
   */
  saving?: boolean;
  /**
   * True while a chat turn is in flight. The landing response replaces the
   * objective, so the row stays read-only rather than opening an editor whose
   * Save is dead.
   */
  locked?: boolean;
  /** Copy for `title` on the disabled edit trigger. */
  lockedReason?: string;
  className?: string;
}

export function StudyObjectivesRow(props: StudyObjectivesRowProps) {
  const {
    goals,
    onUpdateObjective,
    onSaveObjective,
    onEditingChange,
    saving = false,
    locked = false,
    lockedReason,
    className,
  } = props;
  // The row's own words are the builder's, in the interface language like
  // every other control; the objective is the user's text, as written. The
  // questionnaire's language is the respondents' (QA4: after a translation
  // this row read "Objetivos" and "Editar" inside an English builder).
  const { t } = useTranslation("app");

  const decision = (goals?.decision ?? "").trim();
  const source = goals?.source ?? null;
  const isSuggested = source === "inferred";
  const headerLabel =
    source === "confirmed"
      ? t("questionnaire.objectiveOwnTitle")
      : t("questionnaire.objectivesTitle");
  const learn = useMemo(() => cleanLearnBullets(goals?.learn), [goals?.learn]);

  // A suggestion has to be seen, so it opens expanded; a settled objective is
  // one quiet line until asked for. Local only, never persisted.
  const [expanded, setExpanded] = useState(isSuggested);
  const previousSourceRef = useRef(source);
  useEffect(() => {
    // A fresh suggestion landing from the agent re-opens the row for the same
    // reason it opens on mount. Editing one ('inferred' -> 'edited') is not
    // that transition, so Save never collapses the row under the marketer.
    if (source === "inferred" && previousSourceRef.current !== "inferred") {
      setExpanded(true);
    }
    previousSourceRef.current = source;
  }, [source]);

  // null = reading. A string = editing, and it is the whole objective as text.
  const [editorText, setEditorText] = useState<string | null>(null);
  const isEditing = editorText !== null;
  const savedEditorText = useMemo(
    () => serializeStudyObjective({ decision, learn }),
    [decision, learn],
  );
  // Opening the local editor is not an edit. The continuation guard only owns
  // a literal change from the objective it opened with; this is formatting
  // text we already serialize, never an interpretation of the objective.
  const hasUnsavedEdit = editorText !== null && editorText !== savedEditorText;

  // The save runs one render late on purpose: `onSaveObjective` closes over the
  // workspace draft of the render that created it, so calling it in the same
  // tick as the update would persist the objective we just replaced.
  const saveRef = useRef(onSaveObjective);
  useEffect(() => {
    saveRef.current = onSaveObjective;
  });
  // The editor's text at Save, handed back when nothing was sent.
  const savedTextRef = useRef<string | null>(null);
  const saveResultRef = useRef<((sent: boolean) => void) | null>(null);
  const [saveRequests, setSaveRequests] = useState(0);
  useEffect(() => {
    if (saveRequests === 0) {
      return;
    }
    const text = savedTextRef.current;
    void Promise.resolve(saveRef.current()).then(
      (sent) => {
        const committed = sent !== false;
        if (!committed && text !== null) setEditorText(text);
        saveResultRef.current?.(committed);
        saveResultRef.current = null;
      },
      () => {
        if (text !== null) setEditorText(text);
        saveResultRef.current?.(false);
        saveResultRef.current = null;
      },
    );
  }, [saveRequests]);
  useEffect(
    () => () => {
      saveResultRef.current?.(false);
      saveResultRef.current = null;
    },
    [],
  );

  const validation = useMemo(
    () => validateStudyObjectiveText(editorText ?? ""),
    [editorText],
  );
  const editorLength = useMemo(
    () => countStudyObjectiveChars(editorText ?? ""),
    [editorText],
  );

  function openEditor() {
    setEditorText(serializeStudyObjective({ decision, learn }));
    setExpanded(true);
  }

  function cancelEdit() {
    // Restores the saved copy exactly: the editor held a copy, nothing was
    // written to the draft while it was open.
    setEditorText(null);
  }

  function commit(update: StudyObjectiveUpdate) {
    onUpdateObjective(update);
    setSaveRequests((count) => count + 1);
  }

  function savePendingEdit(): Promise<boolean> {
    if (!hasUnsavedEdit || !validation.valid) {
      return Promise.resolve(false);
    }
    const parsed = parseStudyObjective(editorText);
    savedTextRef.current = editorText;
    return new Promise((resolve) => {
      saveResultRef.current = resolve;
      setEditorText(null);
      commit({ decision: parsed.decision, learn: parsed.learn, source: "edited" });
    });
  }

  const savePendingEditRef = useRef(savePendingEdit);
  useEffect(() => {
    savePendingEditRef.current = savePendingEdit;
  });
  useEffect(() => {
    onEditingChange?.(
      hasUnsavedEdit,
      hasUnsavedEdit ? () => savePendingEditRef.current() : undefined,
    );
    return () => onEditingChange?.(false);
  }, [hasUnsavedEdit, onEditingChange]);

  function handleSave() {
    void savePendingEdit();
  }

  if (!decision) {
    return null;
  }

  return (
    <div
      className={cn(
        "group mb-4 overflow-hidden rounded-[12px] border border-border/60 bg-white dark:bg-card",
        className,
      )}
      data-testid="questionnaire-objectives-row"
    >
      {/* The whole line reads as one control; the pencil cannot live inside the
          toggle button, so the padding and hover tint sit on the row instead. */}
      <div className="flex items-center gap-2 px-4 py-2.5 transition-smooth hover:bg-muted/40 dark:hover:bg-white/[0.03]">
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          data-testid="questionnaire-objectives-toggle"
        >
          <Crosshair
            className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <Text
            as="span"
            className="whitespace-nowrap text-[12px] font-medium leading-none text-muted-foreground"
          >
            {headerLabel}
          </Text>
          {!expanded ? (
            <>
              <span className="text-muted-foreground/60" aria-hidden="true">
                ·
              </span>
              <Text
                as="span"
                className={cn(
                  "min-w-0 flex-1 truncate text-[13px] leading-none",
                  isSuggested ? "text-muted-foreground" : "text-foreground",
                )}
                data-testid="questionnaire-objectives-decision"
              >
                {decision}
              </Text>
            </>
          ) : null}
          <ChevronDown
            className={cn(
              "ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground transition-smooth",
              expanded && "rotate-180",
            )}
            aria-hidden="true"
          />
        </button>
        {expanded && !isEditing && !isSuggested ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0 rounded-[6px] opacity-0 transition-smooth focus-visible:opacity-100 group-hover:opacity-100"
            onClick={openEditor}
            aria-label={t("questionnaire.editObjectives")}
            disabled={locked}
            title={locked ? lockedReason : undefined}
            data-testid="questionnaire-objectives-edit"
          >
            <PencilLine className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>

      <div
        className={cn(
          "grid overflow-hidden motion-reduce:transition-none",
          "transition-[grid-template-rows,opacity] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]",
          expanded
            ? "grid-rows-[1fr] opacity-100"
            : "pointer-events-none grid-rows-[0fr] opacity-0",
        )}
        aria-hidden={!expanded}
        data-testid="questionnaire-objectives-body"
      >
        <div className="min-h-0 overflow-hidden">
          <div className="px-5 pb-4 pt-0.5">
          {isEditing ? (
            <div className="animate-fade-in motion-reduce:animate-none">
              <Textarea
                value={editorText ?? ""}
                onChange={(event) => setEditorText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Escape") {
                    event.stopPropagation();
                    cancelEdit();
                  }
                }}
                autoFocus
                placeholder={t("questionnaire.objectivePlaceholder")}
                className="mt-1.5 min-h-[96px] resize-none text-[14px] leading-[1.6]"
                style={{ fieldSizing: "content" } as React.CSSProperties}
                data-testid="questionnaire-objectives-editor"
              />
              <div className="mt-2 flex items-center gap-3">
                {validation.error ? (
                  <Text
                    as="span"
                    className="text-[12px] leading-none text-destructive"
                    data-testid="questionnaire-objectives-error"
                  >
                    {validation.error}
                  </Text>
                ) : null}
                {editorLength >= COUNTER_VISIBLE_FROM ? (
                  <Text
                    as="span"
                    className={cn(
                      "text-[11px] leading-none tabular-nums",
                      editorLength > STUDY_OBJECTIVE_MAX_CHARS
                        ? "text-destructive"
                        : "text-muted-foreground",
                    )}
                    data-testid="questionnaire-objectives-counter"
                  >
                    {editorLength} / {STUDY_OBJECTIVE_MAX_CHARS}
                  </Text>
                ) : null}
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-7 rounded-md px-3 text-xs"
                    onClick={cancelEdit}
                    data-testid="questionnaire-objectives-cancel"
                  >
                    {t("questionnaire.cancel")}
                  </Button>
                  <Button
                    type="button"
                    variant="cta"
                    className="h-7 rounded-md px-3 text-xs"
                    onClick={handleSave}
                    disabled={!hasUnsavedEdit || !validation.valid || saving || locked}
                    data-testid="questionnaire-objectives-save"
                  >
                    {t("questionnaire.save")}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="animate-fade-in motion-reduce:animate-none">
              <div
                className="mt-1"
                data-testid={expanded ? "questionnaire-objectives-decision" : undefined}
              >
                <MorphText
                  value={decision}
                  className={cn(
                    "text-[15px] leading-[1.5]",
                    isSuggested
                      ? "font-normal text-muted-foreground"
                      : "font-medium text-foreground",
                  )}
                />
              </div>

              {learn.length > 0 ? (
                <ul
                  className="mt-2 space-y-1"
                  data-testid="questionnaire-objectives-learn"
                >
                  {learn.map((bullet, index) => (
                    <li
                      key={`${index}-${bullet}`}
                      className={cn(
                        "flex items-start gap-2 text-[13px] leading-[1.6] transition-colors",
                        isSuggested ? "text-muted-foreground" : "text-foreground",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-2 h-1 w-1 shrink-0 rounded-full",
                          isSuggested
                            ? "bg-muted-foreground/60"
                            : "bg-foreground/60",
                        )}
                        aria-hidden="true"
                      />
                      <MorphText value={bullet} as="span" />
                    </li>
                  ))}
                </ul>
              ) : null}

              {isSuggested ? (
                <div className="mt-3.5 flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-7 rounded-md px-3 text-xs"
                    onClick={openEditor}
                    disabled={locked}
                    title={locked ? lockedReason : undefined}
                    data-testid="questionnaire-objectives-edit"
                  >
                    {t("questionnaire.edit")}
                  </Button>
                </div>
              ) : null}
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
